import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';
import type {
	CancelSubscriptionPayload,
	UpdateSubscriptionPayload,
} from 'pkg/actions/subscriptions/interface';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create } from 'pkg/api/models/actions';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

import { Event, trackEvent } from 'components/analytics';

import {
	formatLockTime,
	isCheckoutLocked,
} from 'components/payment_platform/checkout_lock';

export type {
	CancelSubscriptionPayload,
	UpdateSubscriptionPayload,
} from 'pkg/actions/subscriptions/interface';

export interface OrderSubscriptionRow {
	priceId: number;
	quantity: number;
	taxRateId?: number;
	validTo?: number;
}

export interface CreateSubscriptionPayload {
	userIds: number[];
	recipientType: string;
	daysUntilDue: number;
	description?: string;
	stripeCouponId?: string;
	billingUserId?: number;
	collectionMethod: 'manual' | 'automatic';
	startAt?: number;
	endAt?: number;
	installmentCount?: number;
	discountId?: number;
	finalizeNow?: boolean;
	finalizeFirstOrderNow?: boolean;

	type: models.subscription.SubscriptionTypes;
	rows: OrderSubscriptionRow[];
}

export const createBatch = async (
	organizationId: number,
	payload: CreateSubscriptionPayload
): Promise<Response> => {
	const [request, subscriptions, error] = await create<
		CreateSubscriptionPayload,
		models.subscription.Subscription[]
	>(endpoints.Subscriptions.CreateBatch(organizationId), payload);

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: error.error,
		});

		return request;
	}

	// One event per batch, as the generic create event was. Every subscription
	// in a batch shares the club's currency; country is the first customer's.
	trackEvent(Event.SubscriptionCreated, {
		country: subscriptions[0]?.country,
		currency: subscriptions[0]?.currency,
	});

	return request;
};

// A subscription refusal carries no lock end in production, so the page passes the one its subscription DTO holds.
// The lock refusal is a 409; any other failure keeps its own error even while the lock holds.
function showRefusal(
	status: number,
	error: string,
	lockedUntil?: number | null
) {
	if (status === 409 && isCheckoutLocked(lockedUntil)) {
		flashActions.show({
			title: t(
				'Someone is paying an order on this subscription right now. It is locked until {time}.',
				{ time: formatLockTime(lockedUntil) }
			),
		});

		return;
	}

	flashActions.show({
		title: crash().title,
		message: error,
	});
}

export const cancel = async (
	organizationId: number,
	payload: CancelSubscriptionPayload,
	lockedUntil?: number | null
): Promise<boolean> => {
	const request = await sdk.post(
		endpoints.Subscriptions.Cancel(organizationId),
		{},
		payload
	);
	const response = await request.json();

	if (!request.ok) {
		showRefusal(request.status, response.error, lockedUntil);

		return false;
	}

	// Cancellation runs as a job, so it is not done when this returns.
	flashActions.show({
		title: t(`Cancellation started`),
		message: t(`This is being processed and will update shortly.`),
	});

	trackEvent(Event.SubscriptionCancel);

	return true;
};

export const update = async (
	organizationId: number,
	id: number,
	payload: UpdateSubscriptionPayload,
	lockedUntil?: number | null
): Promise<boolean> => {
	const request = await sdk.patch(
		endpoints.Subscriptions.Update(organizationId, id),
		{},
		payload
	);
	const response = await request.json();

	if (!request.ok) {
		showRefusal(request.status, response.error, lockedUntil);

		return false;
	}

	return true;
};

export const updateProductRecipient = async (
	organizationId: number,
	subscriptionId: number,
	userId: number
): Promise<boolean> => {
	const request = await sdk.post(
		endpoints.Subscriptions.UpdateProductRecipient(
			organizationId,
			subscriptionId
		),
		{},
		{ userId }
	);
	const response = await request.json();

	if (request.ok) {
		flashActions.show({
			title: t('Successfully changed product recipient'),
		});

		return true;
	}

	flashActions.show({
		title: crash().title,
		message: response.error,
	});

	return false;
};

export const setPaymentMethod = async (
	organizationId: number,
	subscriptionId: number,
	paymentMethodId: number
): Promise<boolean> => {
	const request = await sdk.post(
		endpoints.Subscriptions.SetPaymentMethod(organizationId, subscriptionId),
		{},
		{ user_payment_method_id: paymentMethodId }
	);
	const response = await request.json();

	if (!request.ok) {
		flashActions.show({
			title: crash().title,
			message: response.error,
		});

		return false;
	}

	return true;
};
